import React, { useState } from 'react';
import ReactDOM from 'react-dom';

// GTM DataLayer helper function
const pushToDataLayer = (eventData) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(eventData);
  }
};

const BathroomLeadPopup = ({ isOpen, onClose, modalZIndex = 1100 }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    zipCode: '',
    renovationPlan: '',
    propertyType: '',
    homeOwnership: '',
    showerBathUpdates: '',
    newFloorplan: '',
    sinkUpdates: '',
    flooring: '',
    cabinets: '',
    countertops: '',
    toiletUpdates: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    consent: false
  });
  const [confirmationUrl, setConfirmationUrl] = useState(null);

  const closePopup = () => {
    // Track popup close
    pushToDataLayer({
      event: 'lead_form_close',
      form_step: currentStep,
      form_completion_rate: (currentStep / 7) * 100
    });
    if (onClose) onClose();
  };

  const nextStep = () => {
    if (currentStep < 7) {
      const newStep = currentStep + 1;
      
      // Track form step completion
      pushToDataLayer({
        event: 'lead_form_step_complete',
        form_step: currentStep,
        next_step: newStep,
        form_completion_rate: (newStep / 7) * 100,
        step_data: getStepData(currentStep)
      });
      
      setCurrentStep(newStep);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      const newStep = currentStep - 1;
      
      // Track form step back navigation
      pushToDataLayer({
        event: 'lead_form_step_back',
        from_step: currentStep,
        to_step: newStep
      });
      
      setCurrentStep(newStep);
    }
  };

  // Helper function to get relevant data for current step
  const getStepData = (step) => {
    switch (step) {
      case 1: return { zipCode: formData.zipCode };
      case 2: return { renovationPlan: formData.renovationPlan };
      case 3: return { propertyType: formData.propertyType, homeOwnership: formData.homeOwnership };
      case 4: return { showerBathUpdates: formData.showerBathUpdates, newFloorplan: formData.newFloorplan };
      case 5: return { 
        sinkUpdates: formData.sinkUpdates, 
        flooring: formData.flooring,
        cabinets: formData.cabinets,
        countertops: formData.countertops,
        toiletUpdates: formData.toiletUpdates
      };
      case 6: return { hasContactInfo: !!(formData.firstName && formData.lastName && formData.email && formData.phone) };
      case 7: return { hasAddress: !!(formData.address && formData.city && formData.state), consent: formData.consent };
      default: return {};
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Track lead form start submission
    pushToDataLayer({
      event: 'lead_form_submit_start',
      lead_value: 50, // Estimated lead value - adjust as needed
      lead_type: 'bathroom_remodel',
      user_data: {
        zip_code: formData.zipCode,
        renovation_plan: formData.renovationPlan,
        property_type: formData.propertyType,
        home_ownership: formData.homeOwnership
      }
    });

    // Map formData to Networx API fields
    const networxPayload = {
      nx_access_key: 'e55ad03895',
      nx_userId: '11414',
      f_name: formData.firstName,
      l_name: formData.lastName,
      zipcode: formData.zipCode,
      task_id: '9', // TODO: Confirm correct task_id for bathroom remodel
      phone: formData.phone.replace(/\D/g, ''),
      email: formData.email,
      tcpa_compliance_text: 'By clicking Get My Quotes, I authorize Pulse Capture, its contractors, and partner companies to contact me about home improvement offers by phone calls, emails, and text messages to the number and email address I provided. I authorize that these marketing communications may be delivered to me using an automatic telephone dialing system or by prerecorded message. I understand that my consent is not a condition of purchase of any goods or services and that I may revoke my consent at any time. I understand that standard message and data rates may apply. I have read and agree to the Privacy Policy, Terms, CCPA Privacy Rights, and Partner List.',
      cert_url: 'https://localbathquotes.com/', // TODO: Replace with actual TrustedForm cert URL if available
      address: formData.address,
      comments: '', // Optionally add more info
      city: formData.city,
      state: formData.state,
      postalCode: formData.postalCode
    };

    try {
      // 1. Send to Pipedream for logging/storage/forwarding
      await fetch('https://eod6sx5ljau0s2x.m.pipedream.net', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, networxPayload })
      });

      // 2. Send to Networx API (POST)
      const params = new URLSearchParams(networxPayload).toString();
      const networxRes = await fetch(`https://api.networx.com?${params}`, { method: 'POST' });
      const xml = await networxRes.text();
      
      // Track successful lead conversion
      pushToDataLayer({
        event: 'conversion',
        event_category: 'Lead Generation',
        event_action: 'Form Submission',
        event_label: 'Bathroom Remodel Lead',
        value: 50,
        currency: 'USD',
        lead_type: 'bathroom_remodel',
        lead_id: Date.now().toString(), // Simple lead ID
        user_data: {
          email: formData.email,
          phone: formData.phone,
          first_name: formData.firstName,
          last_name: formData.lastName,
          address: {
            street: formData.address,
            city: formData.city,
            state: formData.state,
            postal_code: formData.postalCode
          }
        }
      });

      // Track Google Ads conversion
      pushToDataLayer({
        event: 'ads_conversion_lead_submission',
        send_to: 'AW-CONVERSION_ID/CONVERSION_LABEL', // Will be configured in GTM
        value: 50,
        currency: 'USD',
        transaction_id: Date.now().toString()
      });

      // Parse <redirectUrl> from XML
      const match = xml.match(/<redirectUrl>(.*?)<\/redirectUrl>/);
      if (match && match[1]) {
        const decodedUrl = decodeURIComponent(match[1]);
        setConfirmationUrl(decodedUrl);
        
        // Track successful redirect
        pushToDataLayer({
          event: 'lead_redirect_success',
          redirect_url: decodedUrl
        });
      } else {
        // Track completion without redirect
        pushToDataLayer({
          event: 'lead_submission_complete',
          completion_type: 'no_redirect'
        });
        
        alert('Thank you! Your request has been submitted.');
        closePopup();
      }
    } catch (err) {
      // Track submission error
      pushToDataLayer({
        event: 'lead_form_error',
        error_message: err.message,
        error_step: 'submission'
      });
      
      alert('Submission failed. Please try again.');
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1: return formData.zipCode !== '';
      case 2: return formData.renovationPlan !== '';
      case 3: return formData.propertyType !== '' && formData.homeOwnership !== '';
      case 4: return formData.showerBathUpdates !== '' && formData.newFloorplan !== '';
      case 5: return formData.sinkUpdates !== '' && formData.flooring !== '' && formData.cabinets !== '' && formData.countertops !== '' && formData.toiletUpdates !== '';
      case 6: return formData.firstName && formData.lastName && formData.phone && formData.email;
      case 7: return formData.address && formData.city && formData.state && formData.postalCode && formData.consent;
      default: return false;
    }
  };

  if (!isOpen) return null;

  if (confirmationUrl) {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 flex items-center justify-center p-4 bg-black bg-opacity-60" style={{ zIndex: modalZIndex }}>
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl h-[700px] flex flex-col items-center justify-center">
          <div style={{height: 650, width: '100%'}}>
            <iframe id="networx-frame" src={confirmationUrl + '&color=89B8F6'} width="100%" height="100%" title="Networx Confirmation" />
          </div>
          <button className="mt-4 px-6 py-2 bg-[#89B8F6] text-white rounded-lg" onClick={closePopup}>Close</button>
        </div>
      </div>,
      document.body
    );
  }

  // Modal content
  const modalContent = (
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: modalZIndex, background: 'rgba(0,0,0,0.5)', pointerEvents: isOpen ? 'auto' : 'none' }}>
      <div className="bg-white rounded-xl shadow-2xl w-[90vw] h-[90vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="bg-[#89B8F6] text-white p-6 rounded-t-xl relative flex-shrink-0">
          <button
            onClick={closePopup}
            className="absolute top-6 right-6 text-white hover:text-gray-200 text-3xl font-bold leading-none"
          >
            ×
          </button>
          <div className="text-center pr-12">
            <div className="flex justify-center mb-4">
              {[1, 2, 3, 4, 5, 6, 7].map((step) => (
                <div
                  key={step}
                  className={`w-3 h-3 rounded-full mx-1 ${
                    step <= currentStep ? 'bg-white' : 'bg-blue-300'
                  }`}
                />
              ))}
            </div>
            <p className="text-lg opacity-90">Step {currentStep} of 7</p>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-8 flex flex-col justify-center">
          {/* Step 1: Zip Code */}
          {currentStep === 1 && (
            <div className="text-center max-w-md mx-auto w-full">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
                Your Dream Bathroom Starts Here 🔎
              </h2>
              <h3 className="text-xl md:text-2xl text-gray-600 mb-8">
                What Is Your Zip Code?
              </h3>
              
              <div className="mb-8">
                <label className="block text-lg font-medium text-gray-700 mb-3">
                  Postal/Zip Code
                </label>
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  className="w-full px-4 py-4 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89B8F6] focus:border-transparent"
                  placeholder="Enter your zip code"
                />
              </div>

              <button
                onClick={nextStep}
                disabled={!isStepValid()}
                className={`w-full py-4 text-xl font-semibold rounded-lg transition-colors ${
                  isStepValid()
                    ? 'bg-[#89B8F6] text-white hover:bg-[#78a1db]'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Start
              </button>
            </div>
          )}

          {/* Step 2: Renovation Plans */}
          {currentStep === 2 && (
            <div className="text-center max-w-lg mx-auto w-full">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
                What are Your Renovation Plans?
              </h2>
              <p className="text-lg text-gray-600 mb-8">Please Select One.</p>
              
              <div className="space-y-4 mb-8">
                {['Install', 'Replace', 'Repair'].map((plan) => (
                  <label
                    key={plan}
                    className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-colors text-lg ${
                      formData.renovationPlan === plan
                        ? 'border-[#89B8F6] bg-blue-50 text-[#89B8F6]'
                        : 'border-gray-300 hover:border-[#89B8F6] text-gray-700'
                    }`}
                  >
                    <input
                      type="radio"
                      name="renovationPlan"
                      value={plan}
                      checked={formData.renovationPlan === plan}
                      onChange={(e) => handleInputChange('renovationPlan', e.target.value)}
                      className="sr-only"
                    />
                    <span className="font-medium">{plan}</span>
                  </label>
                ))}
              </div>

              <button
                onClick={nextStep}
                disabled={!isStepValid()}
                className={`w-full py-4 text-xl font-semibold rounded-lg transition-colors ${
                  isStepValid()
                    ? 'bg-[#89B8F6] text-white hover:bg-[#78a1db]'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 3: Property Details */}
          {currentStep === 3 && (
            <div className="text-center max-w-lg mx-auto w-full">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8">
                Can You Share a Little About Your Property?
              </h2>
              
              <div className="space-y-8 mb-8">
                <div>
                  <h3 className="text-lg font-medium text-gray-700 mb-4">
                    Is the property a residential or commercial property?
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {['Residential', 'Commercial'].map((type) => (
                      <label
                        key={type}
                        className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          formData.propertyType === type
                            ? 'border-[#89B8F6] bg-blue-50 text-[#89B8F6]'
                            : 'border-gray-300 hover:border-[#89B8F6] text-gray-700'
                        }`}
                      >
                        <input
                          type="radio"
                          name="propertyType"
                          value={type}
                          checked={formData.propertyType === type}
                          onChange={(e) => handleInputChange('propertyType', e.target.value)}
                          className="sr-only"
                        />
                        <span className="font-medium">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-700 mb-4">
                    Do you own your home?
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {['Own', 'Rented'].map((ownership) => (
                      <label
                        key={ownership}
                        className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          formData.homeOwnership === ownership
                            ? 'border-[#89B8F6] bg-blue-50 text-[#89B8F6]'
                            : 'border-gray-300 hover:border-[#89B8F6] text-gray-700'
                        }`}
                      >
                        <input
                          type="radio"
                          name="homeOwnership"
                          value={ownership}
                          checked={formData.homeOwnership === ownership}
                          onChange={(e) => handleInputChange('homeOwnership', e.target.value)}
                          className="sr-only"
                        />
                        <span className="font-medium">{ownership}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <button
                onClick={nextStep}
                disabled={!isStepValid()}
                className={`w-full py-4 text-xl font-semibold rounded-lg transition-colors ${
                  isStepValid()
                    ? 'bg-[#89B8F6] text-white hover:bg-[#78a1db]'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 4: Project Description */}
          {currentStep === 4 && (
            <div className="text-center max-w-lg mx-auto w-full">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8">
                Please Select Which Best Describes Your Project
              </h2>
              
              <div className="space-y-8 mb-8">
                <div>
                  <h3 className="text-lg font-medium text-gray-700 mb-4">
                    Does the project include shower/bath updates?
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {['Yes', 'No'].map((option) => (
                      <label
                        key={option}
                        className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          formData.showerBathUpdates === option
                            ? 'border-[#89B8F6] bg-blue-50 text-[#89B8F6]'
                            : 'border-gray-300 hover:border-[#89B8F6] text-gray-700'
                        }`}
                      >
                        <input
                          type="radio"
                          name="showerBathUpdates"
                          value={option}
                          checked={formData.showerBathUpdates === option}
                          onChange={(e) => handleInputChange('showerBathUpdates', e.target.value)}
                          className="sr-only"
                        />
                        <span className="font-medium">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-700 mb-4">
                    Does the project include a new floorplan?
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {['Yes', 'No'].map((option) => (
                      <label
                        key={option}
                        className={`flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          formData.newFloorplan === option
                            ? 'border-[#89B8F6] bg-blue-50 text-[#89B8F6]'
                            : 'border-gray-300 hover:border-[#89B8F6] text-gray-700'
                        }`}
                      >
                        <input
                          type="radio"
                          name="newFloorplan"
                          value={option}
                          checked={formData.newFloorplan === option}
                          onChange={(e) => handleInputChange('newFloorplan', e.target.value)}
                          className="sr-only"
                        />
                        <span className="font-medium">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <button
                onClick={nextStep}
                disabled={!isStepValid()}
                className={`w-full py-4 text-xl font-semibold rounded-lg transition-colors ${
                  isStepValid()
                    ? 'bg-[#89B8F6] text-white hover:bg-[#78a1db]'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 5: Project Details */}
          {currentStep === 5 && (
            <div className="text-center max-w-2xl mx-auto w-full">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8">
                Please Provide Just a Few More Project Details!
              </h2>
              
              <div className="space-y-6 mb-8">
                {[
                  { key: 'sinkUpdates', label: 'Does the project include sink updates?' },
                  { key: 'flooring', label: 'Does the project include flooring?' },
                  { key: 'cabinets', label: 'Does the project include cabinets?' },
                  { key: 'countertops', label: 'Does the project include countertops?' },
                  { key: 'toiletUpdates', label: 'Does the project include toilet updates?' }
                ].map((item) => (
                  <div key={item.key}>
                    <h3 className="text-lg font-medium text-gray-700 mb-3">
                      {item.label}
                    </h3>
                    <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                      {['Yes', 'No'].map((option) => (
                        <label
                          key={option}
                          className={`flex items-center justify-center p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                            formData[item.key] === option
                              ? 'border-[#89B8F6] bg-blue-50 text-[#89B8F6]'
                              : 'border-gray-300 hover:border-[#89B8F6] text-gray-700'
                          }`}
                        >
                          <input
                            type="radio"
                            name={item.key}
                            value={option}
                            checked={formData[item.key] === option}
                            onChange={(e) => handleInputChange(item.key, e.target.value)}
                            className="sr-only"
                          />
                          <span className="font-medium">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={nextStep}
                disabled={!isStepValid()}
                className={`w-full max-w-md py-4 text-xl font-semibold rounded-lg transition-colors ${
                  isStepValid()
                    ? 'bg-[#89B8F6] text-white hover:bg-[#78a1db]'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 6: Personal Information */}
          {currentStep === 6 && (
            <div className="text-center max-w-lg mx-auto w-full">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8">
                Can You Tell us a Little about Yourself?
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-left text-lg font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89B8F6] focus:border-transparent"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-left text-lg font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89B8F6] focus:border-transparent"
                      placeholder="Doe"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-left text-lg font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89B8F6] focus:border-transparent"
                    placeholder="(555) 123-4567"
                  />
                </div>
                
                <div>
                  <label className="block text-left text-lg font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89B8F6] focus:border-transparent"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <button
                onClick={nextStep}
                disabled={!isStepValid()}
                className={`w-full py-4 text-xl font-semibold rounded-lg transition-colors ${
                  isStepValid()
                    ? 'bg-[#89B8F6] text-white hover:bg-[#78a1db]'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 7: Final Details & Consent */}
          {currentStep === 7 && (
            <div className="text-center max-w-lg mx-auto w-full">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8">
                Before We Match You..
              </h2>
              
              <div className="space-y-6 mb-8">
                <div>
                  <label className="block text-left text-lg font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89B8F6] focus:border-transparent"
                    placeholder="123 Main St"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-left text-lg font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89B8F6] focus:border-transparent"
                      placeholder="Portland"
                    />
                  </div>
                  <div>
                    <label className="block text-left text-lg font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89B8F6] focus:border-transparent"
                      placeholder="OR"
                      maxLength="2"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-left text-lg font-medium text-gray-700 mb-2">
                    Postal/Zip Code
                  </label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                    className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#89B8F6] focus:border-transparent"
                    placeholder="97201"
                  />
                </div>
                
                <div className="text-left">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.consent}
                      onChange={(e) => handleInputChange('consent', e.target.checked)}
                      className="mt-1 mr-3 h-5 w-5 text-[#89B8F6] border-2 border-gray-300 rounded focus:ring-[#89B8F6]"
                    />
                    <span className="text-sm text-gray-600 leading-relaxed">
                      By clicking <strong>Get My Quotes</strong>, I authorize Pulse Capture, its contractors, and partner companies to contact me about home improvement offers by phone calls, emails, and text messages to the number and email address I provided. I authorize that these marketing communications may be delivered to me using an automatic telephone dialing system or by prerecorded message. I understand that my consent is not a condition of purchase of any goods or services and that I may revoke my consent at any time. I understand that standard message and data rates may apply. I have read and agree to the Privacy Policy, Terms, CCPA Privacy Rights, and Partner List.
                    </span>
                  </label>
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={!isStepValid()}
                className={`w-full py-4 text-xl font-semibold rounded-lg transition-colors ${
                  isStepValid()
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Get My Quotes
              </button>
            </div>
          )}
        </div>

        {/* Navigation Footer */}
        {currentStep > 1 && (
          <div className="p-6 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={prevStep}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-lg"
            >
              ← Back
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default BathroomLeadPopup;